/*
 * SKU-first search (added 2026-09-11).
 * Shopify search ranks look-alike part numbers above the one searched (TOW4469 above TOW446), and
 * Search & Discovery product boosts do not change that on this store. When the search term looks like a
 * part number, this script asks sections/sku-family.liquid which listings carry that exact SKU (or
 * SKU-suffix) and shows them first:
 *   - on /search: moved to the top of page 1 (duplicates removed from later "show more" pages)
 *   - in the predictive-search dropdown: listed first
 * Normal word searches, a non-relevance sort order and filtered searches are left exactly as Shopify returns them.
 */
(() => {
  const SKU_RE = /^[A-Z]{1,8}\d{2,}[A-Z]{0,3}(-[A-Z0-9]{1,6})?$/i;
  const MAX_PAGES = 8; // helper pages of 50 results -> scans the first 400 results
  const DROPDOWN_LIMIT = 8; // same as resources[limit] in the theme's predictive search form
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cache = new Map();

  const familyFor = (rawTerm) => {
    const term = (rawTerm || '').trim();
    if (!SKU_RE.test(term)) return Promise.resolve(null);
    const key = term.toUpperCase();
    if (!cache.has(key)) cache.set(key, loadFamily(term).catch(() => null));
    return cache.get(key);
  };

  async function loadFamily(term) {
    const handles = new Set();
    const cards = [];
    const dropdown = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
      const url = `${root}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last&type=product&section_id=sku-family&page=${page}`;
      const response = await fetch(url);
      if (!response.ok) break;
      const holder = new DOMParser().parseFromString(await response.text(), 'text/html').getElementById('sku-family');
      if (!holder) break;
      holder.querySelectorAll('template[data-sku-card]').forEach((card) => {
        const handle = card.dataset.handle;
        if (handles.has(handle)) return;
        handles.add(handle);
        cards.push(card);
        const row = holder.querySelector(`template[data-sku-predictive][data-handle="${CSS.escape(handle)}"]`);
        if (row) dropdown.push(row);
      });
      if (page >= Number(holder.dataset.pages || 1)) break;
    }
    return { term: term.toUpperCase(), handles, cards, dropdown };
  }

  const handleFromHref = (href) => ((href || '').split('?')[0].split('/products/')[1] || '').replace(/\/$/, '');

  /* ---------- /search results grid ---------- */
  async function enhanceResultsGrid() {
    if (!/\/search\/?$/.test(location.pathname) || !document.querySelector('ul#product-grid')) return;
    const family = await familyFor(new URLSearchParams(location.search).get('q'));
    if (!family || !family.cards.length) return;

    const apply = () => {
      const params = new URLSearchParams(location.search);
      const sort = params.get('sort_by');
      if (sort && sort !== 'relevance') return;
      if ([...params.keys()].some((key) => key.startsWith('filter.'))) return;
      if ((params.get('q') || '').trim().toUpperCase() !== family.term) return;
      const grid = document.querySelector('ul#product-grid');
      if (!grid) return;

      // Hide (never remove) the original copies: assets/infinite-scroll.js appends "show more" results after
      // the last item it remembered, so that element must stay in the DOM.
      grid.querySelectorAll('li.grid__item:not([data-sku-first]):not([data-sku-dupe])').forEach((li) => {
        const handle = li.querySelector('[data-product-handle]')?.dataset.productHandle;
        if (!handle || !family.handles.has(handle)) return;
        li.dataset.skuDupe = '1';
        li.style.setProperty('display', 'none', 'important');
      });

      // Family goes on top of page 1 only; later pages just hide their copies.
      if (Number(params.get('page') || 1) === 1 && !grid.querySelector('li[data-sku-first]')) {
        const fragment = document.createDocumentFragment();
        family.cards.forEach((card) => fragment.append(document.importNode(card.content, true)));
        fragment.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.remove('scroll-trigger'));
        grid.prepend(fragment);
      }
    };

    apply();
    const container = document.getElementById('ProductGridContainer') || document.body;
    new MutationObserver(apply).observe(container, { childList: true, subtree: true });
  }

  /* ---------- predictive-search dropdown ---------- */
  function injectDropdown(results, family) {
    const wrapper = results.querySelector('#predictive-search-results');
    if (!wrapper) return;
    let list = results.querySelector('#predictive-search-results-products-list');
    if (!list) {
      results.querySelector('.predictive-search__search-no-results')?.remove();
      let groups = results.querySelector('#predictive-search-results-groups-wrapper');
      if (!groups) {
        groups = document.createElement('div');
        groups.id = 'predictive-search-results-groups-wrapper';
        groups.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
        wrapper.prepend(groups);
      }
      groups.classList.remove('predictive-search__results-groups-wrapper--no-products');
      const group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      group.innerHTML = '<div><ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group"></ul></div>';
      groups.prepend(group);
      list = group.querySelector('ul');
    }

    list.querySelectorAll('li:not([data-sku-first])').forEach((li) => {
      if (family.handles.has(handleFromHref(li.querySelector('a')?.getAttribute('href')))) li.remove();
    });
    const fragment = document.createDocumentFragment();
    family.dropdown.slice(0, DROPDOWN_LIMIT).forEach((row, i) => {
      const node = document.importNode(row.content, true);
      node.firstElementChild.id = `predictive-search-option-sku-${i + 1}`;
      fragment.append(node);
    });
    list.prepend(fragment);
    [...list.children].slice(DROPDOWN_LIMIT).forEach((li) => li.remove());
  }

  // The header search can be inserted after page load (desktop-only templates), so bind lazily the first
  // time a predictive-search input is focused or typed in, instead of scanning the DOM once at start-up.
  function bindDropdown(element) {
    if (!element || element.dataset.skuFirstBound) return;
    const results = element.querySelector('[data-predictive-search]');
    const input = element.querySelector('input[type="search"]');
    if (!results || !input) return;
    element.dataset.skuFirstBound = '1';

    const enhance = async () => {
      const term = input.value.trim();
      if (!SKU_RE.test(term) || !results.querySelector('#predictive-search-results') || results.querySelector('[data-sku-first]')) return;
      const family = await familyFor(term);
      if (!family || !family.dropdown.length || input.value.trim() !== term || results.querySelector('[data-sku-first]')) return;
      injectDropdown(results, family);
    };
    new MutationObserver(enhance).observe(results, { childList: true, subtree: true });
    enhance();
  }

  function enhanceDropdowns() {
    document.querySelectorAll('predictive-search').forEach(bindDropdown);
    const onUse = (event) => bindDropdown(event.target.closest && event.target.closest('predictive-search'));
    document.addEventListener('focusin', onUse, true);
    document.addEventListener('input', onUse, true);
  }

  const start = () => {
    enhanceResultsGrid();
    enhanceDropdowns();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
